/**
 * StripoWrapper component
 *
 * Manages the Stripo editor lifecycle:
 * - Initialization (reuses editor instance when possible)
 * - Save/compile operations
 * - Editor readiness state
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { StripoEditor } from './StripoEditor';
import { useStripoToken } from './useStripoToken';
import { initStripo } from './stripoUtils';
import { TEMPLATE_MERGE_FIELDS } from './stripoMergeFields';
import type { StripoTemplateData } from './stripo.types';
import styles from './Stripo.module.css';

interface StripoWrapperProps {
  html: string;
  css: string;
  showEditor: boolean;
  setShowEditor: (show: boolean) => void;
  saveClicked: boolean;
  setSaveClicked: (clicked: boolean) => void;
  cancelClicked: boolean;
  setCancelClicked: (clicked: boolean) => void;
  onSave: (html: string, css: string, compiledHtml: string) => void;
  onEditorReadyChange?: (isReady: boolean) => void;
  setShowUnsavedChangesDialog: (show: boolean) => void;
  setCheckForUnsavedChanges?: (checkFn: () => boolean) => void;
}

export const StripoWrapper: React.FC<StripoWrapperProps> = ({
  html,
  css,
  showEditor,
  setShowEditor,
  saveClicked,
  setSaveClicked,
  cancelClicked,
  setCancelClicked,
  onSave,
  onEditorReadyChange,
  setShowUnsavedChangesDialog,
  setCheckForUnsavedChanges,
}) => {
  const [isEditorReady, setIsEditorReady] = useState(false);
  const [stripoEmailSaved, setStripoEmailSaved] = useState(false);
  const [stripoEmailCompiled, setStripoEmailCompiled] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const editorReadyCheckInterval = useRef<ReturnType<typeof setInterval> | null>(null);
  const initializingRef = useRef(false);

  const { data: tokenData, isLoading: isTokenLoading, error: tokenError } = useStripoToken();

  const checkEditorReady = useCallback(() => {
    const apiIsReady = !!(
      window.StripoEditorApi?.actionsApi &&
      typeof window.StripoEditorApi.actionsApi.save === 'function' &&
      typeof window.StripoEditorApi.actionsApi.compileEmail === 'function' &&
      typeof window.StripoEditorApi.actionsApi.getTemplateData === 'function'
    );

    if (apiIsReady && !isEditorReady) {
      setIsEditorReady(true);
      onEditorReadyChange?.(true);

      if (editorReadyCheckInterval.current) {
        clearInterval(editorReadyCheckInterval.current);
        editorReadyCheckInterval.current = null;
      }
    }

    return apiIsReady;
  }, [isEditorReady, onEditorReadyChange]);

  // Function to check for unsaved changes
  const checkForUnsavedChanges = useCallback(() => {
    if (isEditorReady && window.StripoEditorApi?.actionsApi?.isAllDataSaved) {
      return !window.StripoEditorApi.actionsApi.isAllDataSaved();
    }
    return false;
  }, [isEditorReady]);

  // Expose checkForUnsavedChanges to parent
  useEffect(() => {
    if (setCheckForUnsavedChanges) {
      setCheckForUnsavedChanges(checkForUnsavedChanges);
    }
  }, [checkForUnsavedChanges, setCheckForUnsavedChanges, isEditorReady]);

  const resetAndCloseEditor = useCallback(() => {
    if (editorReadyCheckInterval.current) {
      clearInterval(editorReadyCheckInterval.current);
      editorReadyCheckInterval.current = null;
    }

    setSaveClicked(false);
    setCancelClicked(false);
    setStripoEmailSaved(false);
    setStripoEmailCompiled(false);
    setIsEditorReady(false);
    onEditorReadyChange?.(false);
    initializingRef.current = false;

    setShowEditor(false);
  }, [setSaveClicked, setCancelClicked, onEditorReadyChange, setShowEditor]);

  // Execute save operation
  const executeSave = useCallback(async () => {
    if (!isEditorReady) {
      console.warn('Save operation blocked - editor not ready yet');
      return;
    }

    if (!window.StripoEditorApi?.actionsApi) {
      console.warn('Save operation blocked - StripoEditorApi not available');
      return;
    }

    try {
      await new Promise<void>((resolve, reject) => {
        let templateData: StripoTemplateData | null = null;
        let compiledHtml: string | null = null;
        let saveCompleted = false;

        const checkAndFinish = () => {
          if (templateData && compiledHtml && saveCompleted) {
            onSave(templateData.html, templateData.css, compiledHtml);
            setStripoEmailCompiled(true);
            resolve();
          }
        };

        // Get template data (HTML and CSS separately)
        window.StripoEditorApi!.actionsApi!.getTemplateData!(data => {
          templateData = data;
          checkAndFinish();
        });

        // Compile email to get compiled HTML
        window.StripoEditorApi!.actionsApi!.compileEmail!({
          callback: (error, compiledHtmlResult) => {
            if (error) {
              reject(new Error(`Compile failed: ${error}`));
              return;
            }
            compiledHtml = compiledHtmlResult;
            checkAndFinish();
          },
          minimize: true,
          mergeTags: [],
          forceAmp: false,
          resetDataSavedFlag: false,
          disableLineHeightsReplace: true,
        });

        window.StripoEditorApi!.actionsApi!.save!(error => {
          if (error) {
            reject(new Error(`Save failed: ${error}`));
            return;
          }
          setStripoEmailSaved(true);
          saveCompleted = true;
          checkAndFinish();
        });
      });
    } catch (error) {
      console.error('Save operation failed:', error);
    }
  }, [isEditorReady, onSave]);

  // Handle save click
  useEffect(() => {
    if (saveClicked && isEditorReady) {
      executeSave();
    } else if (saveClicked && !isEditorReady) {
      console.warn('Save clicked but editor not ready - ignoring');
      setSaveClicked(false);
    }
  }, [saveClicked, executeSave, isEditorReady, setSaveClicked]);

  // Handle save completion
  useEffect(() => {
    if ((stripoEmailSaved && stripoEmailCompiled) || !showEditor) {
      resetAndCloseEditor();
    }
  }, [stripoEmailSaved, stripoEmailCompiled, showEditor, resetAndCloseEditor]);

  // Handle cancel click
  useEffect(() => {
    if (cancelClicked) {
      if (checkForUnsavedChanges()) {
        setShowUnsavedChangesDialog(true);
        return;
      }
      resetAndCloseEditor();
    }
  }, [cancelClicked, checkForUnsavedChanges, resetAndCloseEditor, setShowUnsavedChangesDialog]);

  // Initialize Stripo editor when dialog opens
  useEffect(() => {
    if (!tokenData?.token || !containerRef.current || !showEditor || initializingRef.current) {
      return;
    }

    const initializeEditor = async () => {
      initializingRef.current = true;

      try {
        // Generate a unique emailId for this session
        const emailId = `partner-template-${Date.now()}`;

        await initStripo({
          emailId,
          html: html || '',
          css: css || '',
          container: containerRef.current!,
          mergeFields: TEMPLATE_MERGE_FIELDS,
          getToken: async () => tokenData.token,
        });

        // Start checking for editor readiness
        editorReadyCheckInterval.current = setInterval(() => {
          checkEditorReady();
        }, 300);
      } catch (error) {
        console.error('Failed to initialize Stripo editor:', error);
        initializingRef.current = false;
      }
    };

    initializeEditor();
  }, [tokenData, showEditor, html, css, checkEditorReady]);

  useEffect(() => {
    return () => {
      if (editorReadyCheckInterval.current) {
        clearInterval(editorReadyCheckInterval.current);
      }
    };
  }, []);

  if (tokenError) {
    return (
      <div className={styles.wrapperCentered}>
        <p>Failed to load editor</p>
        <button onClick={() => window.location.reload()}>Retry</button>
      </div>
    );
  }

  if (isTokenLoading || !tokenData) {
    return (
      <div className={styles.wrapperCentered}>
        <div className={styles.loadingSpinner} />
        <span>Loading editor...</span>
      </div>
    );
  }

  return (
    <div className={styles.wrapper}>
      <StripoEditor containerRef={containerRef} showSaving={saveClicked && isEditorReady} />
    </div>
  );
};
